import { Injectable } from '@angular/core';
import { ChatDownloadDocument, ChatItem } from '../../../models/chat-ui.model';
import { ChatSource } from '../../../models/chat.model';
import { MappedConversation } from './conversation-mapper.service';
import { ChatMessageTextService } from '../presentation/chat-message-text.service';

@Injectable({
  providedIn: 'root'
})
export class ChatConversationUiMapperService {
  constructor(private messageText: ChatMessageTextService) {}

  mapConversation(
    conversation: MappedConversation[],
    entityIdFactory: () => string,
    normalizeContent: boolean
  ): ChatItem[] {
    return conversation.flatMap((turn, idx) => [
      {
        type: 'user' as const,
        content: this.formatContent(turn.question, normalizeContent),
        timestamp: turn.createdAt,
        id: turn.questionId,
        entityId: entityIdFactory(),
        sources: [],
        // Repinta el badge por-mensaje del rol experto al recargar (solo si se persistio rol).
        ...(turn.expertRole ? { expertRole: turn.expertRole } : {})
      },
      {
        type: 'system' as const,
        content: this.formatContent(turn.answer, normalizeContent),
        timestamp: turn.createdAt,
        id: turn.answerId,
        entityId: entityIdFactory(),
        sources: this.buildSelectedSources(turn, idx),
        downloadDocuments: this.buildDownloadDocuments(turn),
        // Fase 4 (aditivo): solo se copia evidence_status si está presente. Stub seguro:
        // mientras el backend no lo persista por turno, queda undefined (recarga igual que hoy).
        ...(turn.evidenceStatus ? { evidenceStatus: turn.evidenceStatus } : {})
      }
    ]);
  }

  /**
   * Reconstruye la fuente del chunk seleccionado SOLO para el panel de detalles.
   * No incluye los enlaces de descarga (esos van en `downloadDocuments`) para no
   * contaminar la seleccion de fuente ni auto-abrir el panel con datos vacios.
   */
  private buildSelectedSources(turn: MappedConversation, idx: number): ChatSource[] {
    if (!turn.selectedChunk) {
      return [];
    }
    return [{
      id: `selected-chunk-${idx}`,
      original_filename: '',
      title: '',
      content: turn.selectedChunk,
      score: 1,
      location: '',
      chunk: undefined,
      metadata: undefined,
      selected: true
    }];
  }

  /**
   * Documentos descargables del turno recargado. Si el backend persistio
   * `referenced_documents`, se ofrecen TODOS (deduplicados); si no (turnos
   * antiguos), se cae al documento seleccionado (`selected_chunk.metadata`).
   */
  private buildDownloadDocuments(turn: MappedConversation): ChatDownloadDocument[] {
    const referenced = turn.referencedDocuments ?? [];
    if (referenced.length) {
      return referenced.map(document => ({ documentId: document.documentId, filename: document.filename ?? null }));
    }
    if (turn.selectedDocument) {
      return [{ documentId: turn.selectedDocument.documentId, filename: turn.selectedDocument.filename ?? null }];
    }
    return [];
  }

  private formatContent(content: string, normalizeContent: boolean): string {
    return normalizeContent ? this.messageText.normalize(content) : content;
  }
}
